import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Images, Trash2, X } from 'lucide-react';

import { ToolPageHeader } from '../components/tools/ToolPageHeader';
import { ErrorBanner } from '../components/tools/ErrorBanner';
import { Button } from '../components/ui';
import apiService from '../services/apiService';
import type { PictureItem, RecentPartGroup } from '../services/types';

// ── Thumbnail card ──────────────────────────────────────────────────────────
const ThumbnailCard: React.FC<{
  pic: PictureItem;
  draggable?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onDelete: () => void;
  onView: () => void;
}> = ({
  pic,
  draggable: isDraggable,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onDelete,
  onView,
}) => {
  const { t } = useTranslation();

  return (
    <div
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={[
        'relative group border rounded-lg overflow-hidden bg-white shadow-sm transition-all flex-shrink-0 w-[320px]',
        isDraggable ? 'cursor-grab active:cursor-grabbing' : '',
        isDragging ? 'opacity-30 scale-95' : '',
        isDragOver ? 'border-l-4 border-l-blue-500' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isDraggable && (
        <div className="absolute top-1 right-1 bg-black/40 rounded text-white z-10 pointer-events-none p-0.5">
          <GripVertical className="w-4 h-4" />
        </div>
      )}

      <button
        className="block w-full aspect-square"
        onClick={onView}
        aria-label={t('pictureManager.viewFull')}
      >
        <img
          src={pic.thumbnailUrl}
          alt={`${pic.partNumber} #${pic.index}`}
          className="w-full h-full object-cover"
          draggable={false}
        />
      </button>

      {pic.index === 0 && (
        <span className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded font-medium">
          {t('pictureManager.primaryBadge')}
        </span>
      )}

      <div className="px-2 py-1.5 bg-gray-50 border-t flex items-center justify-between">
        <div>
          <span className="text-xs text-gray-500">#{pic.index}</span>
          {pic.lastModified && (
            <p className="text-xs text-gray-400">
              {new Date(pic.lastModified).toLocaleDateString()}
            </p>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={t('pictureManager.deletePicture')}
          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ── Part group row ──────────────────────────────────────────────────────────
const PartGroupRow: React.FC<{
  group: RecentPartGroup;
  onView: (url: string) => void;
  onReorder: (
    partNumber: string,
    newPictures: PictureItem[],
    newOrder: number[]
  ) => void;
  onDelete: (partNumber: string, index: number) => void;
}> = ({ group, onView, onReorder, onDelete }) => {
  const { t } = useTranslation();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => setDragIndex(index);
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const sourceDragIndex = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);

    if (sourceDragIndex === null || sourceDragIndex === dropIndex) return;

    const newPictures = [...group.pictures];
    const [moved] = newPictures.splice(sourceDragIndex, 1);
    newPictures.splice(dropIndex, 0, moved);

    const newOrder = newPictures.map(
      (pic) => group.pictures.findIndex((p) => p === pic) + 1
    );

    onReorder(group.partNumber, newPictures, newOrder);
  };

  const handleDelete = (pic: PictureItem) => {
    if (!window.confirm(t('pictureManager.deleteConfirm'))) return;
    onDelete(group.partNumber, pic.index);
  };

  return (
    <div className="border rounded-lg bg-white shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">
          {group.partNumber}
        </h3>
        <span className="text-xs text-gray-400">
          {new Date(group.mostRecentUpload).toLocaleString()}
        </span>
      </div>
      <p className="text-xs text-gray-400 mb-2">
        {t('pictureManager.dragHint')}
      </p>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {group.pictures.map((pic, i) => (
          <ThumbnailCard
            key={`${pic.partNumber}-${pic.index}`}
            pic={pic}
            draggable
            isDragging={dragIndex === i}
            isDragOver={dragOverIndex === i && dragIndex !== i}
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDrop={(e) => handleDrop(e, i)}
            onDragEnd={handleDragEnd}
            onView={() => onView(pic.fullUrl)}
            onDelete={() => handleDelete(pic)}
          />
        ))}
      </div>
    </div>
  );
};

// ── Main page ───────────────────────────────────────────────────────────────
export const PictureManagerPage: React.FC = () => {
  const { t } = useTranslation();

  const [partGroups, setPartGroups] = useState<RecentPartGroup[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const sentinelRef = useRef<HTMLDivElement>(null);

  // ── Load data ──────────────────────────────────────────────────────────
  const loadPage = useCallback(
    async (pageNum: number, filterValue: string, append: boolean) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await apiService.getRecentParts(
          pageNum,
          10,
          filterValue.trim() || undefined
        );

        if (append) {
          setPartGroups((prev) => [...prev, ...result.parts]);
        } else {
          setPartGroups(result.parts);
        }
        setPage(result.page);
        setTotalPages(result.totalPages);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('pictureManager.loadError')
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [t]
  );

  // Initial load
  useEffect(() => {
    loadPage(1, '', false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Infinite scroll ────────────────────────────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !loading &&
          !loadingMore &&
          page < totalPages
        ) {
          loadPage(page + 1, appliedFilter, true);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [page, totalPages, loading, loadingMore, appliedFilter, loadPage]);

  // ── Filter ─────────────────────────────────────────────────────────────
  const handleFilter = () => {
    setAppliedFilter(filter);
    loadPage(1, filter, false);
  };

  const handleFilterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleFilter();
  };

  // ── Reorder ────────────────────────────────────────────────────────────
  const handleReorder = (
    partNumber: string,
    newPictures: PictureItem[],
    newOrder: number[]
  ) => {
    // Optimistic update
    setPartGroups((prev) =>
      prev.map((g) =>
        g.partNumber === partNumber ? { ...g, pictures: newPictures } : g
      )
    );

    apiService
      .reorderPictures(partNumber, newOrder)
      .then(() => apiService.getPartPictures(partNumber))
      .then((refreshed) => {
        setPartGroups((prev) =>
          prev.map((g) =>
            g.partNumber === partNumber ? { ...g, pictures: refreshed } : g
          )
        );
      })
      .catch((err) => {
        setError(
          err instanceof Error ? err.message : t('pictureManager.reorderError')
        );
        // Reload to revert
        loadPage(1, appliedFilter, false);
      });
  };

  // ── Delete ─────────────────────────────────────────────────────────────
  const handleDelete = async (partNumber: string, index: number) => {
    setError(null);
    try {
      await apiService.deletePicture(partNumber, index);
      const refreshed = await apiService.getPartPictures(partNumber);
      if (refreshed.length === 0) {
        // Part has no more pictures — remove the group
        setPartGroups((prev) =>
          prev.filter((g) => g.partNumber !== partNumber)
        );
      } else {
        setPartGroups((prev) =>
          prev.map((g) =>
            g.partNumber === partNumber ? { ...g, pictures: refreshed } : g
          )
        );
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('pictureManager.deleteError')
      );
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <ToolPageHeader
        icon={Images}
        title={t('pictureManager.title')}
        description={t('pictureManager.description')}
      />

      {/* Filter row */}
      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={handleFilterKey}
          placeholder={t('pictureManager.filterByPart')}
          className="flex-1 border rounded-md px-3 py-2 text-sm"
        />
        <Button onClick={handleFilter} size="sm">
          {t('pictureManager.searchButton')}
        </Button>
      </div>

      {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : partGroups.length === 0 ? (
        <p className="text-sm text-gray-500">
          {t('pictureManager.noRecentPictures')}
        </p>
      ) : (
        <div className="space-y-4">
          {partGroups.map((group) => (
            <PartGroupRow
              key={group.partNumber}
              group={group}
              onView={setLightboxUrl}
              onReorder={handleReorder}
              onDelete={handleDelete}
            />
          ))}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-1" />

          {loadingMore && (
            <p className="text-sm text-gray-500 text-center py-2">
              {t('pictureManager.loadMore')}…
            </p>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/20"
            onClick={() => setLightboxUrl(null)}
            aria-label={t('common.close')}
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxUrl}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
